import { Link } from 'react-router';
import { useMerges, useRestoreRecord } from '../../../data/hooks';
import { Button, cx, useToast } from '../../../ui';

/**
 * THE EXIT ON AN ARCHIVED ROW (admin-area.md D21): Restore, or - when the row is a merge
 * loser - the merge, because that is the operation that actually works.
 *
 * WHY THE MERGE CASE IS KNOWN BEFORE THE CLICK. The record read cannot tell the two apart:
 * with `includeDeleted` the server returns the tombstone WITHOUT `mergedInto`
 * (`records/read.ts` returns early, deliberately, so the loser stays viewable), so an
 * archived merge loser and an ordinary archived row are byte-identical on that response.
 * The merge JOURNAL can tell them apart, and `useMerges` already fetches it: the predicate
 * below - loser id, merge not undone - is character for character the one the server guards
 * restore on (`findOpenMergeOfLoser`, used by `flipDeletion`). So the button that cannot
 * work is never offered, rather than being offered and failing.
 *
 * ONE QUERY FOR THE WHOLE SEGMENT, not one per row: `useMerges({ objectId })` is a single
 * cache key, so every row on screen reads the same entry (INV-13). Scoping it by record id
 * would issue a request per archived row for an answer that is one list.
 *
 * The server's refusal is still rendered if it ever arrives - a merge decided in another tab
 * between the fetch and the click lands there - so the guard is an improvement on the error
 * path, never a replacement for it.
 */
export interface ArchivedRowActionProps {
  readonly objectId: string;
  readonly recordId: string;
  /** Named in the toast: "Dorian Halstead is back" beats "the record is back". */
  readonly title: string;
  /**
   * INSIDE A TABLE ROW, WHERE 54px IS THE WHOLE BUDGET (audit P1-5).
   *
   * A `sm` Button is 32px and the cell's own padding is 15px above and below, so a button
   * here makes a 62px row - and the row it is in must look like the rows around it. The
   * compact shape is the one the merge case already uses on both surfaces: a text action,
   * drawn at its own line height and PRESSED at the product's 40px floor through
   * `.hit-expand`, which exists for exactly this ("some controls are DRAWN smaller than
   * they are pressed"). Desktop-only, because the table is: the phone's card keeps the
   * real button, where a thumb needs a target rather than a word.
   */
  readonly compact?: boolean | undefined;
}

/**
 * The two actions share their look in the compact shape, so a row cannot have two registers.
 *
 * NO UNDERLINE (wave 4, C15; behaviour check (b), "Apple's lists never underline a row on
 * hover"). An underline is a link's promise inside running text, and these two sit in a TABLE
 * ROW, which since L-B1 is a row and not a link. What is left is Apple's own text button: the
 * accent colour and nothing else, at the row's line height, pressed at the 40px floor through
 * `.hit-expand`.
 */
const COMPACT_ACTION =
  'hit-expand shrink-0 rounded-control text-sm whitespace-nowrap text-accent focus-visible:outline-2 focus-visible:outline-accent';

export function ArchivedRowAction({
  objectId,
  recordId,
  title,
  compact = false,
}: ArchivedRowActionProps) {
  const toast = useToast();
  const merges = useMerges({ objectId });
  const restore = useRestoreRecord(objectId);

  const runRestore = () =>
    restore.mutate(recordId, {
      onSuccess: () => toast({ title: `${title} is back`, tone: 'success' }),
      onError: (error) =>
        toast({ title: 'Could not restore', description: error.message, tone: 'danger' }),
    });

  const openMerge = merges.data?.find(
    (merge) => merge.loserRecordId === recordId && merge.undoneAt === null,
  );

  if (openMerge !== undefined) {
    const to = `/objects/${objectId}/records/${openMerge.winnerRecordId}`;
    return compact ? (
      <Link to={to} className={COMPACT_ACTION}>
        Undo merge
      </Link>
    ) : (
      /* `whitespace-nowrap` on both shapes: a wrapped three-word link is where the 127px
         row came from, and a card has no more use for a two-line action than a row does.
         The underline went with the compact shape's (wave 4, C15): the two exits from an
         archived record read as one thing, and neither of them is running text. */
      <Link
        to={to}
        className="inline-flex min-h-(--touch-target-min) shrink-0 items-center text-sm whitespace-nowrap text-accent"
      >
        Undo merge
      </Link>
    );
  }

  return compact ? (
    <button
      type="button"
      disabled={restore.isPending}
      onClick={runRestore}
      className={cx(COMPACT_ACTION, restore.isPending && 'opacity-60')}
    >
      Restore
    </button>
  ) : (
    /* THE DEFAULT SIZE, NOT `sm` (wave 2's ledger item 8, closed in wave 4). This is the
       PHONE's shape - the table takes the compact one above - and a `sm` Button is drawn at
       32px, under the touch floor. The "Undo Merge" link on the same card carried the
       floor all along, so the two exits from an archived card were 32px and 40px beside
       each other; both read `--touch-target-min` now, which is 44 on a phone and 40 on
       desktop (E5). */
    <Button variant="secondary" icon="arrow-up" loading={restore.isPending} onClick={runRestore}>
      Restore
    </Button>
  );
}
